import { useState, useEffect } from 'react';
import { ComponentLifetime, TemplatesByType } from '../../TemplatesEditor/types';
import { Building } from '../../../../types/protocols';
import { MaintenanceDraftWithDone } from '../types';
import {
    loadBuilding,
    loadMaintenancePlanDraft
} from '../services/firebaseService';
import { useBuildComponentTypes } from '../../../../contexts/BuildComponentTypesContext';

interface UseMaintenanceDataReturn {
    building: Building | null;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    maintenanceDraft: MaintenanceDraftWithDone | null;
    loading: boolean;
    refreshDraft: () => Promise<void>;
    setBuilding: React.Dispatch<React.SetStateAction<Building | null>>;
    setComponentLifetimes: React.Dispatch<React.SetStateAction<Record<string, ComponentLifetime>>>;
    setMaintenanceTemplates: React.Dispatch<React.SetStateAction<TemplatesByType>>;
}

/**
 * Custom hook to manage all data loading for maintenance draft
 * Consolidates loading of building, lifetimes, templates and draft
 */
export const useMaintenanceData = (buildingKey: string | undefined): UseMaintenanceDataReturn => {
    const [building, setBuilding] = useState<Building | null>(null);
    const [componentLifetimes, setComponentLifetimes] = useState<Record<string, ComponentLifetime>>({});
    const [maintenanceTemplates, setMaintenanceTemplates] = useState<TemplatesByType>({});
    const [maintenanceDraft, setMaintenanceDraft] = useState<MaintenanceDraftWithDone | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // Get component types data from context
    const { types, getLifetime, getTemplates } = useBuildComponentTypes();
    const hasTypes = Object.keys(types).length > 0;

    const loadAllData = async () => {
        if (!buildingKey) return;

        // Don't start loading if types haven't been loaded yet
        if (!hasTypes) {
            setLoading(true);
            return;
        }

        setLoading(true);
        try {
            const [
                buildingData,
                draft
            ] = await Promise.all([
                loadBuilding(buildingKey),
                loadMaintenancePlanDraft(buildingKey)
            ]);

            setBuilding(buildingData);
            setMaintenanceDraft(draft);
        } catch (error) {
            console.error('Error loading maintenance data:', error);
        } finally {
            setLoading(false);
        }
    };

    const refreshDraft = async () => {
        if (!buildingKey) return;

        try {
            const draft = await loadMaintenancePlanDraft(buildingKey);
            setMaintenanceDraft(draft);
        } catch (error) {
            console.error('Error refreshing draft:', error);
        }
    };

    // Reload the building's data only when the building changes or the types first
    // arrive, not on every template save (which replaces `types`)
    useEffect(() => {
        loadAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildingKey, hasTypes]);

    // Keep the lifetime and template maps in step with the types context, so a
    // template saved from this page is shown as soon as the context refreshes
    useEffect(() => {
        const lifetimes: Record<string, ComponentLifetime> = {};
        const templatesByType: TemplatesByType = {};
        Object.keys(types).forEach(key => {
            const lifetime = getLifetime(key);
            if (lifetime) {
                lifetimes[key] = lifetime;
            }
            const componentTemplates = getTemplates(key);
            if (componentTemplates.length > 0) {
                templatesByType[key] = componentTemplates;
            }
        });
        setComponentLifetimes(lifetimes);
        setMaintenanceTemplates(templatesByType);
    }, [types, getLifetime, getTemplates]);

    return {
        building,
        componentLifetimes,
        maintenanceTemplates,
        maintenanceDraft,
        loading,
        refreshDraft,
        setBuilding,
        setComponentLifetimes,
        setMaintenanceTemplates
    };
};
